import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, EmptyState, List, ListItem, Skeleton } from '@sabay/ui-kit';
import { getGroupons, type GrouponDeal } from '@/lib/api';
import { formatPrice } from '@/lib/format';
import { useTk } from '@/lib/i18n';
import { useSelectedCity } from '@/lib/use-city';
import { BottomNav } from '@/components/BottomNav';

interface LoadState {
  status: 'idle' | 'loading' | 'success' | 'error';
  items: GrouponDeal[];
  errorMessage: string | null;
}

export function GrouponListPage(): React.ReactElement {
  const tk = useTk();
  const [cityCode] = useSelectedCity();
  const [searchParams] = useSearchParams();
  const categoryCode = searchParams.get('categoryCode') ?? undefined;

  const [state, setState] = useState<LoadState>({
    status: 'loading',
    items: [],
    errorMessage: null,
  });

  useEffect(() => {
    const ac = new AbortController();
    setState((s) => ({ ...s, status: 'loading', errorMessage: null }));
    getGroupons({ cityCode, categoryCode, pageSize: 20 }, ac.signal)
      .then((res) =>
        setState({ status: 'success', items: res.items, errorMessage: null }),
      )
      .catch((err: Error) => {
        if (ac.signal.aborted) return;
        setState({ status: 'error', items: [], errorMessage: err.message });
      });
    return () => ac.abort();
  }, [cityCode, categoryCode]);

  return (
    <main className="min-h-screen pb-24">
      <header className="sticky top-0 z-20 px-5 pt-5 pb-3 bg-clay-50/85 backdrop-blur-md space-y-1">
        <span className="eyebrow">Discover · Groupons</span>
        <div className="flex items-end justify-between gap-3">
          <h1 className="display-heading text-[26px] leading-tight">
            {tk('nav.groupon')}
          </h1>
          {categoryCode ? (
            <span className="chip chip-idle">{categoryCode}</span>
          ) : null}
        </div>
      </header>

      <div className="px-5 mt-4">
        {state.status === 'loading' ? (
          <List className="divide-y-0 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <ListItem key={i} className="border-0 p-0">
                <Card variant="raised" padding="sm" className="flex items-stretch gap-3">
                  <Skeleton className="h-24 w-24 shrink-0 rounded-xl" />
                  <div className="flex min-w-0 flex-1 flex-col justify-between gap-2 py-1">
                    <div className="space-y-2">
                      <Skeleton shape="text" className="h-4 w-full" />
                      <Skeleton shape="text" className="h-3 w-2/3" />
                    </div>
                    <div className="flex justify-between gap-2">
                      <Skeleton shape="text" className="h-5 w-24" />
                      <Skeleton className="h-5 w-12 rounded-md" />
                    </div>
                  </div>
                </Card>
              </ListItem>
            ))}
          </List>
        ) : null}

        {state.status === 'error' ? (
          <Card variant="flat" padding="md" className="border-danger-500/40 bg-danger-500/10">
            <p className="text-sm font-medium text-danger-500">{tk('common.error.generic')}</p>
            <p className="mt-1 break-all text-xs text-danger-500 opacity-75">{state.errorMessage}</p>
          </Card>
        ) : null}

        {state.status === 'success' && state.items.length === 0 ? (
          <Card variant="flat" padding="none" className="border-dashed border-clay-200">
            <EmptyState className="py-10" title={tk('common.empty')} />
          </Card>
        ) : null}

        {state.status === 'success' && state.items.length > 0 ? (
          <List className="divide-y-0 gap-3">
            {state.items.map((deal) => (
              <ListItem key={deal.id} className="border-0 p-0">
                <Link to={`/groupons/${deal.id}`} className="block transition active:scale-[0.99]">
                  <Card variant="raised" padding="sm" className="hover:border-amber-300">
                    <div className="flex items-stretch gap-3">
                      <DealThumb title={deal.titleZh} />
                      <div className="flex flex-1 min-w-0 flex-col justify-between py-1">
                        <div className="space-y-1">
                          <h3 className="line-clamp-2 text-[15px] font-medium leading-snug text-clay-900">
                            {deal.titleZh}
                          </h3>
                          <p className="truncate text-xs text-clay-500">{deal.merchant.nameZh}</p>
                        </div>
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex items-baseline gap-2">
                            <span className="price-amount text-lg leading-none">
                              {formatPrice(deal.priceCents, deal.currency)}
                            </span>
                            <span className="price-strike">
                              {formatPrice(deal.originalPriceCents, deal.currency)}
                            </span>
                          </div>
                          <span className="chip bg-amber-50 text-[10px] font-medium uppercase tracking-wider text-amber-700">
                            Deal
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </ListItem>
            ))}
          </List>
        ) : null}
      </div>

      <BottomNav />
    </main>
  );
}

function DealThumb({ title }: { title: string }): React.ReactElement {
  // 商家未提供缩略图前，用首字 + amber 渐变占位（不是图标网格 anti-pattern：每张图都是 unique 第一字）
  const initial = (title || '?').slice(0, 1);
  return (
    <div
      className="relative grid h-24 w-24 shrink-0 place-items-center rounded-xl overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, #FFE3BD 0%, #FFB050 60%, #E5741F 100%)',
      }}
      aria-hidden
    >
      <span className="font-serif text-3xl font-semibold text-clay-900/85">
        {initial}
      </span>
    </div>
  );
}
